import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { ArrowLeft, User, Mail, Calendar, Settings, LogOut } from "lucide-react";
import Navbar from "../components/Navbar";
import { toast } from "sonner";

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, token, logout, updateMode } = useAuth();
  const [selectedMode, setSelectedMode] = useState(user?.mode || "general");
  const [saving, setSaving] = useState(false);

  const handleModeChange = async (mode) => {
    setSelectedMode(mode);
    setSaving(true);
    try {
      await updateMode(mode);
      toast.success("Mode updated successfully");
    } catch (error) {
      toast.error("Failed to update mode");
      setSelectedMode(user?.mode || "general");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const modes = [
    { id: "school", name: "School", desc: "Beginner level content" },
    { id: "college", name: "College", desc: "Intermediate academic level" },
    { id: "general", name: "General Practice", desc: "Everyday English improvement" },
    { id: "ssc", name: "SSC", desc: "Staff Selection Commission prep" },
    { id: "banking", name: "Banking", desc: "Bank exam preparation" },
    { id: "upsc", name: "UPSC", desc: "Civil Services preparation" },
  ];

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="ghost" onClick={() => navigate("/dashboard")} className="mb-6" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        {/* Profile Header */}
        <Card className="mb-6" data-testid="profile-card">
          <CardContent className="p-8">
            <div className="flex items-center gap-6">
              <Avatar className="w-20 h-20">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="text-2xl bg-slate-200 text-slate-700">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{user?.name}</h1>
                <div className="flex items-center gap-2 text-slate-600 mt-1">
                  <Mail className="w-4 h-4" />
                  <span>{user?.email}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-500 mt-1 text-sm">
                  <Calendar className="w-4 h-4" />
                  <span>
                    Member since{" "}
                    {user?.created_at
                      ? new Date(user.created_at).toLocaleDateString("en-US", {
                          month: "long",
                          year: "numeric",
                        })
                      : "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Exam Mode Settings */}
        <Card className="mb-6" data-testid="mode-settings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Exam Mode
            </CardTitle>
            <CardDescription>
              Choose your target exam to get personalized content recommendations
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-3">
              {modes.map((mode) => (
                <div
                  key={mode.id}
                  onClick={() => handleModeChange(mode.id)}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedMode === mode.id
                      ? "border-slate-900 bg-slate-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`mode-option-${mode.id}`}
                >
                  <p className="font-medium text-slate-900">{mode.name}</p>
                  <p className="text-sm text-slate-500">{mode.desc}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Account Actions */}
        <Card data-testid="account-actions-card">
          <CardHeader>
            <CardTitle>Account</CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              variant="destructive"
              onClick={handleLogout}
              className="w-full sm:w-auto"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ProfilePage;
